import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Box, Typography, Grid, Card, CardContent, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, Button, TextField, MenuItem, CircularProgress,
  LinearProgress, IconButton, Badge, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import {
  TrendingUp, AccountBalanceWallet, People, Receipt,
  Refresh, CheckCircle, Cancel, HourglassEmpty, FiberManualRecord
} from '@mui/icons-material';
import AdminLayout from '../../components/admin/AdminLayout';
import {
  fetchAdminRevenueSummary, fetchAdminAllPayouts, fetchAstrologerEarningsList,
  fetchActiveConsultations
} from '../../store/slices/earningsSlice';
import { approveWithdrawal, rejectWithdrawal } from '../../store/slices/walletSlice';

const fmt = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const StatCard = ({ title, value, icon, color = 'primary.main' }) => (
  <Card elevation={2}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Box sx={{ color, mr: 1 }}>{icon}</Box>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
      </Box>
      <Typography variant="h5" fontWeight="bold">{value}</Typography>
    </CardContent>
  </Card>
);

const statusColor = (status) => {
  if (status === 'completed') return 'success';
  if (status === 'rejected') return 'error';
  if (status === 'processing') return 'warning';
  return 'default';
};

const statusIcon = (status) => {
  if (status === 'completed') return <CheckCircle fontSize="small" />;
  if (status === 'rejected') return <Cancel fontSize="small" />;
  return <HourglassEmpty fontSize="small" />;
};

const callTypeLabel = (type) => {
  if (type === 'call') return 'Voice';
  if (type === 'video') return 'Video';
  return 'Chat';
};

export default function RevenueDashboard() {
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [period, setPeriod] = useState('all');
  const [actionLoading, setActionLoading] = useState({});

  const { adminRevenue, adminPayouts, astrologerList, activeConsultations, loading } = useSelector(s => s.earnings);
  const { loading: walletLoading } = useSelector(s => s.wallet);

  const pendingCount = (adminPayouts?.payouts || []).filter(p => p.status === 'pending_approval' || p.status === 'pending').length;
  const liveCount = activeConsultations?.count || (activeConsultations?.consultations || []).length;

  const refresh = useCallback(() => {
    dispatch(fetchAdminRevenueSummary({ period }));
    dispatch(fetchAdminAllPayouts(statusFilter ? { status: statusFilter } : {}));
    dispatch(fetchAstrologerEarningsList());
    dispatch(fetchActiveConsultations());
  }, [dispatch, period, statusFilter]);

  // Initial load
  useEffect(() => {
    refresh();
  }, [dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-fetch revenue when period changes
  useEffect(() => {
    dispatch(fetchAdminRevenueSummary({ period }));
  }, [period, dispatch]);

  // Re-fetch payouts when status filter changes
  useEffect(() => {
    dispatch(fetchAdminAllPayouts(statusFilter ? { status: statusFilter } : {}));
  }, [statusFilter, dispatch]);

  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(refresh, 60000);
    return () => clearInterval(id);
  }, [refresh]);

  // Refresh live sessions every 30s when on that tab
  useEffect(() => {
    if (tab !== 2) return;
    const id = setInterval(() => dispatch(fetchActiveConsultations()), 30000);
    return () => clearInterval(id);
  }, [tab, dispatch]);

  const handleApprove = async (payoutId) => {
    setActionLoading(prev => ({ ...prev, [payoutId]: 'approving' }));
    await dispatch(approveWithdrawal(payoutId));
    setActionLoading(prev => ({ ...prev, [payoutId]: null }));
    dispatch(fetchAdminAllPayouts(statusFilter ? { status: statusFilter } : {}));
    dispatch(fetchAdminRevenueSummary({ period }));
  };

  const handleReject = async (payoutId) => {
    const reason = window.prompt('Rejection reason (optional):') ?? '';
    setActionLoading(prev => ({ ...prev, [payoutId]: 'rejecting' }));
    await dispatch(rejectWithdrawal({ id: payoutId, reason }));
    setActionLoading(prev => ({ ...prev, [payoutId]: null }));
    dispatch(fetchAdminAllPayouts(statusFilter ? { status: statusFilter } : {}));
  };

  return (
    <AdminLayout title="Revenue Dashboard">
      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" fontWeight="bold">Revenue Dashboard</Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={(_, v) => v && setPeriod(v)}
              size="small"
            >
              <ToggleButton value="today">Today</ToggleButton>
              <ToggleButton value="week">This Week</ToggleButton>
              <ToggleButton value="month">This Month</ToggleButton>
              <ToggleButton value="all">All Time</ToggleButton>
            </ToggleButtonGroup>
            <IconButton onClick={refresh} title="Refresh"><Refresh /></IconButton>
          </Box>
        </Box>

        {/* Platform Stats */}
        {loading.adminRevenue ? <LinearProgress sx={{ mb: 2 }} /> : adminRevenue && (
          <>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total GMV" value={fmt(adminRevenue.total_gmv)} icon={<TrendingUp />} color="primary.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Platform Revenue (30%)" value={fmt(adminRevenue.platform_revenue)} icon={<Receipt />} color="success.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Astrologer Payouts (70%)" value={fmt(adminRevenue.astrologer_payouts)} icon={<AccountBalanceWallet />} color="info.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Active Astrologers" value={adminRevenue.active_astrologers ?? 0} icon={<People />} color="warning.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Completed Consultations" value={adminRevenue.completed_consultations ?? 0} icon={<CheckCircle />} color="success.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Recharged" value={fmt(adminRevenue.total_recharged)} icon={<AccountBalanceWallet />} color="primary.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Withdrawn" value={fmt(adminRevenue.total_withdrawn)} icon={<Receipt />} color="warning.main" />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <StatCard title="Total Balance in Wallets" value={fmt(adminRevenue.total_balance_held)} icon={<AccountBalanceWallet />} color="info.main" />
              </Grid>
            </Grid>

            {/* Wallet balance breakdown */}
            {(adminRevenue.user_balance_held !== undefined) && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Wallet Balance Breakdown</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip label={`Users: ${fmt(adminRevenue.user_balance_held)}`} variant="outlined" size="small" color="primary" />
                  <Chip label={`Astrologers: ${fmt(adminRevenue.astrologer_balance_held)}`} variant="outlined" size="small" color="success" />
                  <Chip label={`Platform: ${fmt(adminRevenue.platform_balance_held)}`} variant="outlined" size="small" color="secondary" />
                  {adminRevenue.total_frozen > 0 && (
                    <Chip label={`Frozen (pending payouts): ${fmt(adminRevenue.total_frozen)}`} variant="outlined" size="small" color="warning" />
                  )}
                </Box>
              </Box>
            )}

            {/* Transaction breakdown */}
            {adminRevenue.transaction_breakdown && Object.keys(adminRevenue.transaction_breakdown).length > 0 && (
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>Transaction Breakdown</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {Object.entries(adminRevenue.transaction_breakdown).map(([type, data]) => (
                    <Chip
                      key={type}
                      label={`${type}: ${fmt(data.total)} (${data.count})`}
                      variant="outlined"
                      size="small"
                    />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}

        {/* Tabs */}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab
            label={
              pendingCount > 0
                ? <Badge badgeContent={pendingCount} color="error" sx={{ pr: 1 }}>Payout Requests</Badge>
                : 'Payout Requests'
            }
          />
          <Tab label="Astrologer Earnings" />
          <Tab
            label={
              liveCount > 0
                ? <Badge badgeContent={liveCount} color="success" sx={{ pr: 1 }}>Live Sessions</Badge>
                : 'Live Sessions'
            }
          />
        </Tabs>

        {/* Tab 0: Payout Requests */}
        {tab === 0 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                select size="small" label="Status Filter" value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                sx={{ minWidth: 160 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="pending_approval">Pending Approval</MenuItem>
                <MenuItem value="processing">Processing</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </TextField>
            </Box>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Astrologer</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading.adminPayouts && (
                    <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                  )}
                  {!loading.adminPayouts && (adminPayouts?.payouts || []).length === 0 && (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>No payout requests found.</TableCell></TableRow>
                  )}
                  {(adminPayouts?.payouts || []).map(p => (
                    <TableRow key={p.id}>
                      <TableCell>{new Date(p.created_at).toLocaleDateString('en-IN')}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.astrologer_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.astrologer_email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{p.bank_name || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">{p.account_number} {p.ifsc ? `• ${p.ifsc}` : ''}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(p.amount)}</TableCell>
                      <TableCell>
                        <Chip
                          icon={statusIcon(p.status)}
                          label={p.status}
                          color={statusColor(p.status)}
                          size="small"
                        />
                        {p.rejection_reason && (
                          <Typography variant="caption" color="error" display="block">{p.rejection_reason}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {(p.status === 'pending_approval' || p.status === 'pending') && (
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Button
                              size="small" variant="contained" color="success"
                              disabled={!!actionLoading[p.id]}
                              onClick={() => handleApprove(p.id)}
                            >
                              {actionLoading[p.id] === 'approving' ? '...' : 'Approve'}
                            </Button>
                            <Button
                              size="small" variant="outlined" color="error"
                              disabled={!!actionLoading[p.id]}
                              onClick={() => handleReject(p.id)}
                            >
                              {actionLoading[p.id] === 'rejecting' ? '...' : 'Reject'}
                            </Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Tab 1: Astrologer Earnings */}
        {tab === 1 && (
          <TableContainer component={Paper} elevation={1}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Astrologer</TableCell>
                  <TableCell align="right">Total Earned</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell align="right">Withdrawn</TableCell>
                  <TableCell align="right">Pending Payout</TableCell>
                  <TableCell>PAN</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading.astrologerList && (
                  <TableRow><TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                )}
                {!loading.astrologerList && (astrologerList?.astrologers || []).length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>No astrologers found.</TableCell></TableRow>
                )}
                {(astrologerList?.astrologers || []).map(a => (
                  <TableRow key={a.astrologer_id}>
                    <TableCell>
                      <Typography variant="body2">{a.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{a.email}</Typography>
                    </TableCell>
                    <TableCell align="right">{fmt(a.total_earned)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main' }}>{fmt(a.available_balance)}</TableCell>
                    <TableCell align="right">{fmt(a.total_withdrawn)}</TableCell>
                    <TableCell align="right" sx={{ color: a.pending_payout > 0 ? 'warning.main' : 'text.secondary' }}>
                      {fmt(a.pending_payout)}
                    </TableCell>
                    <TableCell>
                      {a.pan_verified
                        ? <Chip label="Verified" color="success" size="small" icon={<CheckCircle />} />
                        : <Chip label="Not set" size="small" />}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 2: Live Sessions */}
        {tab === 2 && (
          <>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <FiberManualRecord sx={{ color: 'success.main', fontSize: 14 }} />
              <Typography variant="body2" color="text.secondary">
                Auto-refreshes every 30s. Showing {liveCount} active session{liveCount !== 1 ? 's' : ''}.
              </Typography>
            </Box>
            <TableContainer component={Paper} elevation={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Astrologer</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell align="right">Rate/min</TableCell>
                    <TableCell align="right">Billed So Far</TableCell>
                    <TableCell>Started</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading.activeConsultations && (
                    <TableRow><TableCell colSpan={7} align="center"><CircularProgress size={24} /></TableCell></TableRow>
                  )}
                  {!loading.activeConsultations && (activeConsultations?.consultations || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                        No active sessions right now.
                      </TableCell>
                    </TableRow>
                  )}
                  {(activeConsultations?.consultations || []).map(c => (
                    <TableRow key={c.id}>
                      <TableCell>{c.user_name}</TableCell>
                      <TableCell>{c.astrologer_name}</TableCell>
                      <TableCell>
                        <Chip label={callTypeLabel(c.type)} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{c.duration_minutes} min</TableCell>
                      <TableCell align="right">{fmt(c.rate_per_min)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                        {fmt(c.billed_so_far)}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(c.started_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(c.started_at).toLocaleDateString('en-IN')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
    </AdminLayout>
  );
}
